const {
  createMonitor,
  doNotSendError,
  getEnvironmentErrorList,
  getDefaultEnvironment
} = require('@blaze-cms/core-monitors');
const {
  env: { isEnvTrue }
} = require('@blaze-cms/utils');
const {
  BLAZE_SENTRY_ENVIRONMENT,
  BLAZE_SENTRY_ATTACH_STRACKTRACE,
  BLAZE_SENTRY_IGNORE_ERROR_TYPES
} = require('../constants');

const createSentryProcessor = ({ sentry }) => {
  const getEnvironment = () => getDefaultEnvironment(BLAZE_SENTRY_ENVIRONMENT);

  const isConfigured = () => !!process.env.BLAZE_SENTRY_DSN;

  const setup = project => {
    if (!isConfigured()) return;
    // ignoreErrors, denyUrls, beforeSend options in can be use to stop sending certains errors to sentry
    const sentryConfig = {
      dsn: process.env.BLAZE_SENTRY_DSN,
      release: process.env.npm_package_version,
      environment: getEnvironment(),
      debug: isEnvTrue('BLAZE_SENTRY_DEBUG'),
      attachStacktrace: !!BLAZE_SENTRY_ATTACH_STRACKTRACE,
      ignoreErrors: getEnvironmentErrorList(
        'BLAZE_SENTRY_IGNORE_ERROR_TYPES',
        BLAZE_SENTRY_IGNORE_ERROR_TYPES
      ),
      beforeSend: filterError
    };
    sentry.init(sentryConfig);

    sentry.configureScope(scope => {
      scope.setTag('project', project);
    });
  };

  const filterError = event => {
    // Even object is sentry specific. Therefore, validating event exception is not monitor's responsibility
    // Generalized code is in monitor function doNotSendError.
    if (
      !event.exception ||
      !event.exception.values ||
      doNotSendError(event.exception.values[0].value)
    )
      return null; // These errors are not going to sentry.

    return event;
  };

  const log = (data = {}) => {
    if (!isActive()) return;
    const { breadcrumbs = null, tags = null, contexts = {}, error = null, clear = false } = data;

    if (!error) {
      return;
    }

    sentry.withScope(scope => {
      if (clear) {
        // For API clear is true but for UI it is off.
        scope.clear();
        scope.clearBreadcrumbs();
      }

      if (tags) {
        addTags(scope, tags);
      }

      if (breadcrumbs) {
        addBreadcrumbs(scope, breadcrumbs);
      }

      if (contexts) {
        addContexts(scope, contexts);
      }

      sentry.captureException(error);
    });
  };

  const addTags = (scope, tags) => {
    // Tags are key/value string pairs that are indexed and searchable.
    // They power UI features like filters and tag-distribution maps.
    // https://docs.sentry.io/enriching-error-data/additional-data/?platform=node
    Object.keys(tags).forEach(tag => {
      if (tags[tag]) {
        scope.setTag(tag, tags[tag]);
      }
    });
  };

  const addBreadcrumbs = (scope, breadcrumbs) => {
    // Sentry supports a concept called Breadcrumbs, which is a trail of events which
    // happened prior to an issue. Often times these events are very similar to traditional logs,
    // but also have the ability to record more rich structured data.
    // https://docs.sentry.io/enriching-error-data/breadcrumbs/?platform=node
    breadcrumbs.forEach(breadcrumb => {
      scope.addBreadcrumb({
        category: breadcrumb.category,
        message: breadcrumb.message,
        level: sentry.Severity.Info
      });
    });
  };

  const addContexts = (scope, contexts) => {
    // Typically, this is data related to the current user and the environment.
    // For example, the device or application version.
    // https://develop.sentry.dev/sdk/event-payloads/contexts/
    Object.keys(contexts).forEach(key => {
      if (contexts[key]) {
        scope.setContext(key, contexts[key]);
      }
    });
  };

  const isActive = () => isConfigured(); // Placeholder. Paid plugins can be made inactive after configuration.

  return createMonitor({
    getEnvironment,
    isConfigured,
    setup,
    log,
    isActive,
    filterError
  });
};

module.exports = createSentryProcessor;
